import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IdLabel, KiTranslatedBadge, StateChip } from '../../components/atoms.js';
import { type CatalogColumn, CatalogTable, columnWidth } from '../../components/catalog-table.js';
import { SearchEmptyNotice } from '../../components/search-empty-notice.js';
import { TestCasePicker, type TestCasePickerCandidate } from '../../components/test-case-picker.js';
import { ClipTooltip } from '../../components/tooltip.js';
import { assignmentApi } from '../../lib/assignment-api.js';
import { type E2eProcess, catalogApi } from '../../lib/catalog-api.js';
import { mapApiError } from '../../lib/errors.js';
import { pickLang } from '../../lib/pick-lang.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { structureApi } from '../../lib/structure-api.js';
import { toast } from '../../lib/toast.js';
import { useShowSourceRef } from '../../lib/use-source-ref.js';
import { useUrlState } from '../../lib/use-url-state.js';

// #52: E2E-Prozesse waren der letzte Katalog-Tab mit einem eigenen Bedienkonzept (Kartenliste
// mit eigenem Auf-/Zuklappen). Jetzt liefert der Tab seine Spalten an CatalogTable wie die
// anderen drei — nur die Mitgliederliste bleibt unveraendert: sie ist eine geordnete Beziehung
// (Testfaelle in Sequenz), keine Tabellenspalte, und sitzt deshalb im `expanded`-Slot statt in
// einer Zelle. `expandedId` ersetzt das bisherige Pro-Karte-useState — es kann immer nur eine
// Kette gleichzeitig aufgeklappt sein (Akkordeon), wie es die Kartenliste vorher auch schon war.
//
// human_id (Nummernkreis) und die Mitgliederanzahl sind feste Felder (kein `editor`): sie kommen
// nicht aus dem Entwurf, sondern werden aus der Zeile abgeleitet bzw. sind serverseitig vergeben.
// Kein mapError: E2E-Ketten haben keinen Unique-Schluessel — zwei Ketten duerfen denselben Namen
// tragen, ihre IDs unterscheiden sie.

// #112 (Abnahme-Befund 1): `node_id` liegt im Entwurf wie jedes andere Feld der Zeile — die
// Zuweisung des Streams laeuft damit ueber genau denselben Weg wie name_de/name_en (Doppelklick
// oder Bearbeiten-Knopf, Enter speichert). Beim SCHREIBEN trennen sich die Wege wieder, siehe
// `onUpdate`/`onCreate` weiter unten.
type Draft = { name_de: string; name_en: string; node_id: string | null };

/** Der Namensteil des Entwurfs — alles, was ueber die Katalog-Route geht. */
type NameDraft = Pick<Draft, 'name_de' | 'name_en'>;

// I4 (Review-Finding): Breiten getrennt exportiert, damit catalog-table.test.tsx
// computeTableMinWidth gegen die ECHTEN Breiten dieses Tabs pruefen kann. Anders als bei Glossar/
// Testdaten/Vereinfachungen hat hier JEDE Spalte eine eigene Breite (kein #81a-Unsized-Risiko) —
// genau das macht den I3-Fix noetig (siehe computeTableMinWidth in catalog-table.tsx).
export const E2E_COLUMN_WIDTHS: (string | undefined)[] = ['16%', '18%', '26%', '26%', '8%'];

// #82: Ersetzt den 204er-<select>, dessen Tastatursuche nur am Textanfang matchte (jede Option
// beginnt mit "ETO-" — "42" tippen landete nirgends). Sucht jetzt per matchesQuery (Task 3) ueber
// ID UND beide Titel, Teiltreffer statt nur Praefix.
//
// Review-Fund (Important 2, Task 5 #87/#105): der eigentliche Combobox-/Filter-/Auswahl-Aufbau
// lebt seit dem Review als `TestCasePicker` in components/test-case-picker.tsx — eine fast
// identische Kopie war fuer die „blockiert außerdem"-Pflege (#105) entstanden. `AddMemberPicker`
// bleibt als duenner Wrapper bestehen (Name + Signatur unveraendert, exportiert wie zuvor), damit
// die bestehenden e2e-tab.test.tsx-Assertions auf die E2E-eigenen Texte
// (`catalog.e2e.memberSearch*`, `catalog.actions.add`) unveraendert weiter greifen — nur DIESE
// drei Texte sind der variable Teil, den `TestCasePicker` jetzt als Props statt fest verdrahtet
// entgegennimmt.
export function AddMemberPicker({
  pending,
  candidates,
  onAdd,
}: {
  pending: boolean;
  candidates: TestCasePickerCandidate[];
  onAdd: (testCaseId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <TestCasePicker
      label={t('catalog.e2e.memberSearchLabel')}
      placeholder={t('catalog.e2e.memberSearchPlaceholder')}
      addLabel={t('catalog.actions.add')}
      pending={pending}
      candidates={candidates}
      onAdd={onAdd}
    />
  );
}

// Unveraendert aus der bisherigen Kartenliste uebernommen (Task-Brief): Mitglieder-Reorder folgt
// dem ▲/▼-Muster wie StepTable (kein DnD) und schickt bei jeder Aenderung die volle neue
// Mitgliederliste an setE2eMembers — der Server vergibt die sequence-Werte 0..n-1 strikt aus der
// Array-Reihenfolge, die wir mitschicken. Nur die Huelle ist neu: statt einer immer gemounteten
// Karte mit eigenem Auf-/Zuklapp-State ist dies jetzt die `expanded`-Zeile von CatalogTable, die
// nur gemountet wird, wenn ihre Kette gerade aufgeklappt ist.

// #144: Seit #144 tragen die Testfall-Listen des Prozessbaums Kettenbezüge („DTP_2 · 3/10"). Jede
// Kettenänderung (Glieder, Name/ID, Löschen) muss deshalb neben ['e2e'] auch diese Caches
// aktualisieren. react-query invalidiert per Präfix: ['tcs', projectId] trifft alle
// Prozess-Listen ['tcs', projectId, processId], aber NICHT 'tcs-search' / 'tcs-without-process'.
function invalidateChainCaches(queryClient: QueryClient, projectId: string) {
  for (const key of ['e2e', 'tcs', 'tcs-search', 'tcs-without-process']) {
    void queryClient.invalidateQueries({ queryKey: [key, projectId] });
  }
}

function E2eMembers({
  projectId,
  chain,
  colSpan,
}: {
  projectId: string;
  chain: E2eProcess;
  colSpan: number;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const queryClient = useQueryClient();
  // #112 (A6): derselbe Admin-Schalter wie im Prozessbaum.
  const showSourceRef = useShowSourceRef(projectId);

  // Unfiltert geladen (kein process_id-Query-Param) — liefert alle Testfaelle des Projekts als
  // Kandidaten fuer die Mitgliederauswahl, eigener Query-Key damit die process-gefilterten Listen
  // aus ProcessTree nicht kollidieren.
  const allTestCasesQuery = useQuery({
    queryKey: ['tcs-all', projectId],
    queryFn: () => catalogApi.listTestCases(projectId),
  });

  const invalidate = () => invalidateChainCaches(queryClient, projectId);

  const setMembers = useMutation({
    mutationFn: (members: { test_case_id: string; sequence: number }[]) =>
      catalogApi.setE2eMembers(projectId, chain.e2e_id, members),
    onSuccess: invalidate,
  });

  const ordered = chain.members.slice().sort((a, b) => a.sequence - b.sequence);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= ordered.length) return;
    const reordered = ordered.slice();
    const [moved] = reordered.splice(index, 1);
    if (!moved) return;
    reordered.splice(target, 0, moved);
    setMembers.mutate(reordered.map((m, i) => ({ test_case_id: m.test_case_id, sequence: i })));
  };

  const removeMember = (testCaseId: string) => {
    const remaining = ordered.filter((m) => m.test_case_id !== testCaseId);
    setMembers.mutate(remaining.map((m, i) => ({ test_case_id: m.test_case_id, sequence: i })));
  };

  const addMember = (testCaseId: string) => {
    setMembers.mutate([
      ...ordered.map((m, i) => ({ test_case_id: m.test_case_id, sequence: i })),
      { test_case_id: testCaseId, sequence: ordered.length },
    ]);
  };

  const memberIds = new Set(ordered.map((m) => m.test_case_id));
  // #82: title_en zaehlt jetzt mit in die Kandidatensuche (matchesQuery in AddMemberPicker) —
  // vorher stand hier nur human_id/title_de, weil der alte <select> ohnehin nur title_de anzeigte.
  const candidates = (allTestCasesQuery.data ?? [])
    .filter((tc) => !memberIds.has(tc.test_case_id))
    .map((tc) => ({
      test_case_id: tc.test_case_id,
      human_id: tc.human_id,
      title_de: tc.title_de,
      title_en: tc.title_en,
    }));

  return (
    <tr className="border-b border-[var(--border)] last:border-0">
      <td colSpan={colSpan} className="bg-bg-surface px-3 py-3">
        <div className="space-y-2 pl-6">
          {ordered.length === 0 && (
            <div className="text-sm text-text-muted">{t('catalog.empty')}</div>
          )}
          {ordered.map((m, index) => (
            <div key={m.test_case_id} className="flex items-center gap-2 text-sm">
              <span className="w-6 shrink-0 text-text-muted">{m.sequence + 1}</span>
              {/* #112 (A6): Quell-ID des Testfalls neben der TestFlow-ID — dieselbe Darstellung
                  wie im Prozessbaum und in der Testplan-Auswahl, gesteuert vom selben Schalter. */}
              <IdLabel id={m.human_id} secondary={showSourceRef ? m.source_ref : null} />
              {/* #95: Titel steht neben Auf-/Ab-/Entfernen-Schaltflaechen, nicht in einer von
                  ihnen — kein fokussierbares Kind zum Umschliessen. tabIndex macht den Titel
                  selbst zum Fokus-Ziel fuer die ClipTooltip-Blase. */}
              <ClipTooltip text={pickLang(lang, m.title_de, m.title_en)} className="min-w-0 flex-1">
                {/* biome-ignore lint/a11y/noNoninteractiveTabindex: reiner Lesetext ohne
                    Klickziel — der Tab-Stop existiert nur fuer die ClipTooltip-Blase (#95). */}
                <span data-clip tabIndex={0} className="block truncate text-text-body">
                  {pickLang(lang, m.title_de, m.title_en)}
                </span>
              </ClipTooltip>
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0 || setMembers.isPending}
                className="rounded-btn px-1 text-text-muted hover:text-text-body disabled:opacity-30"
                aria-label={t('catalog.actions.moveUp')}
                title={t('catalog.actions.moveUp')}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === ordered.length - 1 || setMembers.isPending}
                className="rounded-btn px-1 text-text-muted hover:text-text-body disabled:opacity-30"
                aria-label={t('catalog.actions.moveDown')}
                title={t('catalog.actions.moveDown')}
              >
                ▼
              </button>
              <button
                type="button"
                onClick={() => removeMember(m.test_case_id)}
                disabled={setMembers.isPending}
                className="rounded-btn px-1 text-text-muted hover:text-status-fail"
                aria-label={t('catalog.actions.delete')}
                title={t('catalog.actions.delete')}
              >
                ✕
              </button>
            </div>
          ))}

          <AddMemberPicker
            pending={setMembers.isPending}
            candidates={candidates}
            onAdd={addMember}
          />
        </div>
      </td>
    </tr>
  );
}

export function E2eTab({ projectId, q }: { projectId: string; q?: string | undefined }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  // Gleiches Muster wie in den drei Geschwister-Tabs — die Katalog-Tabs zeigen ihre
  // Bedienelemente jedem und der Server antwortet dem Betrachter mit 403.
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // #125 (Q3): derselbe Admin-Schalter wie bei Testfällen — die Ketten-ID ist die Quell-ID der Kette.
  const showSourceRef = useShowSourceRef(projectId);

  const { data: chains, isLoading } = useQuery({
    queryKey: ['e2e', projectId],
    queryFn: () => catalogApi.listE2e(projectId),
  });

  // #78: Freitextfilter ueber die bereits geladenen Ketten (kein neuer Endpunkt, kein Refetch) —
  // Task-Brief-Vorgabe fuer die "uebrigen Tabs". Sucht ueber die sichtbaren Spalten (ID + beide
  // Namen). matchesQuery (Task 3) buendelt die Regel (getrimmt, kleingeschrieben, kein Fuzzy).
  const trimmedQuery = (q ?? '').trim();
  const searching = trimmedQuery.length > 0;
  const rows = chains ?? [];

  // 2. Abnahme, Befund 4: „ich kann hier weder nach Stream filtern, noch sind die E2E-Tests nach
  // Stream gruppiert". Beides kommt jetzt — und zwar beides, weil sie verschiedene Fragen
  // beantworten und einzeln jeweils die andere offen ließen:
  //   Gruppierung  = „wie ist der Katalog gegliedert?" (die SICHT aus A3, Stream → E2E-Prozess)
  //   Stream-Filter = „ich arbeite jetzt nur an O2C" (alles andere weg, auch beim Suchen)
  // Nur die Gruppierung hätte den Nutzer bei 40 Ketten weiter scrollen lassen, nur der Filter
  // hätte die Sicht aus A3 nicht hergestellt (eine gefilterte flache Liste ist keine Gliederung).
  // Beide Zustände liegen in der URL (Muster #36/#84) — deep-linkbar und reload-fest wie der
  // Reiter der Scope-Auswahl.
  const [streamParam, setStreamParam] = useUrlState('e2e_stream', 'all');
  // Vorgabe „gruppiert": A3 verlangt die Sicht, nicht die Option darauf. Abschaltbar bleibt sie
  // trotzdem — beim Vergleichen quer über alle Streams stört jede Zwischenüberschrift.
  const [groupParam, setGroupParam] = useUrlState('e2e_group', 'stream');
  const grouped = groupParam !== 'none';

  const filteredRows = useMemo(() => {
    let base = searching
      ? rows.filter((r) =>
          matchesQuery(trimmedQuery, r.human_id, r.name_de, r.name_en, r.source_ref),
        )
      : rows;
    // 'none' = „Ohne Stream" (A2: ein zulässiger Zustand, also ein regulärer Filterwert und kein
    // Sonderfall im Kleingedruckten), 'all' = kein Filter.
    if (streamParam === 'none') base = base.filter((r) => r.node_id === null);
    else if (streamParam !== 'all') base = base.filter((r) => r.node_id === streamParam);
    // #112: nach Stream sortiert, damit die zweite Sicht (Stream > E2E-Prozess) in der flachen
    // Tabelle sichtbar wird — gleiche Streams stehen beieinander, Ketten ohne Stream am Ende.
    // Innerhalb eines Streams bleibt die Ordnung des Servers (human_id). Die Sortierung bleibt
    // auch bei ausgeschalteter Gruppierung: sie ist die Grundlage der Gruppenbildung (erstes
    // Auftreten gewinnt) und ohne sie zerfiele ein Stream in mehrere Gruppen.
    return [...base].sort((a, b) => {
      const an = a.node_name ?? '￿';
      const bn = b.node_name ?? '￿';
      if (an !== bn) return an.localeCompare(bn);
      return a.human_id.localeCompare(b.human_id);
    });
  }, [rows, searching, trimmedQuery, streamParam]);

  // #112 (Abnahme-Befund 1): die Streams des Projekts als Optionen der Stream-Spalte. Gleicher
  // Query-Key wie Admin-Panel/Zuordnungsseite/Scope-Tabelle — ein neu angelegter Stream steht hier
  // dadurch ohne eigenes Zutun zur Auswahl.
  const { data: nodes } = useQuery({
    queryKey: ['structure-nodes', projectId],
    queryFn: () => structureApi.list(projectId),
    enabled: Boolean(projectId),
  });
  const streamOptions = useMemo(
    () =>
      (nodes ?? [])
        .filter((n) => n.kind === 'stream')
        .map((n) => ({ value: n.node_id, label: n.name })),
    [nodes],
  );

  const invalidate = () => invalidateChainCaches(queryClient, projectId);

  // errorToast: false auf allen drei Mutationen — CatalogTable zeigt Anlegen-/Bearbeiten- und
  // Loesch-Fehler bereits namentlich in der Zeile (Spec Kapitel 2), der globale Toast waere hier
  // die doppelte, generische zweite Meldung (#52-Review Finding 1).
  const createChain = useMutation({
    mutationFn: (d: NameDraft) => catalogApi.createE2e(projectId, d),
    meta: { successToast: 'feedback.created', errorToast: false },
    onSuccess: invalidate,
  });
  // #112 (Abnahme-Befund 1) — der Stream-Schreibpfad. Bewusst NICHT die Katalog-Route:
  //
  // Kante C (Kette → anderer Stream) muss den Sign-off-Widerruf aus A8 ausloesen. Die Regel sitzt
  // serverseitig in `applyAssignment`; die frueher hier benutzte Route PATCH /e2e/:id/stream ist
  // genau deshalb entfallen (zwei Wege fuer eine Operation, einer davon ohne Widerruf). Die Zeile
  // schickt ihren Stream deshalb an POST /assignment/apply — ein Feld der Tabelle mit einer
  // anderen Route als die uebrigen. Das ist der kleinste Weg, der BEIDE Bedingungen erfuellt:
  // Bedienung in der Tabelle (Befund 1) und Widerruf nach A8.
  //
  // CatalogTable laesst das ohne Umbau zu: `onUpdate`/`onCreate` sind Callbacks des Tabs, die
  // Tabelle kennt keine Route. Sie sieht nur ein Promise — erfuellt = Zeile schliesst,
  // abgelehnt = Zeile bleibt offen mit Fehler.
  //
  // Abweichung von „ein Bedienmuster fuer alle drei Kanten" (Architektur-Leitentscheidung 3): Die
  // Vorschau VOR dem Klick bleibt der Zuordnungsseite vorbehalten (dort werden Mengen bewegt).
  // Hier faellt die Meldung dafuer NACH dem Speichern und nennt `revoked_count` beim Namen —
  // verschluckt wird der Widerruf an keiner Stelle.
  const moveStream = useMutation({
    mutationFn: ({ id, target }: { id: string; target: string | null }) =>
      assignmentApi.apply(projectId, { kind: 'e2e', ids: [id], target_id: target }),
    meta: { errorToast: false },
    onSuccess: (res) => {
      toast.success(
        res.revoked_count > 0
          ? t('catalog.e2e.streamSavedRevoked', { revoked: res.revoked_count })
          : t('catalog.e2e.streamSaved'),
      );
      invalidate();
      // Der Widerruf trifft Bestaetigungen und die Zaehler am Stream — beide Sichten nachziehen.
      void queryClient.invalidateQueries({ queryKey: ['structure-nodes', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['signoffs', projectId] });
    },
  });
  const updateChain = useMutation({
    mutationFn: ({ id, d }: { id: string; d: Partial<Draft> }) =>
      catalogApi.updateE2e(projectId, id, d),
    meta: { errorToast: false },
    onSuccess: invalidate,
  });
  const deleteChain = useMutation({
    mutationFn: (id: string) => catalogApi.deleteE2e(projectId, id),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onSuccess: invalidate,
  });
  const columns: CatalogColumn<E2eProcess, Draft>[] = [
    {
      key: 'human_id',
      header: t('catalog.fields.id'),
      ...columnWidth(E2E_COLUMN_WIDTHS[1]),
      // #49 (LNG-4): Prüfmarkierung aus dem Import als Row-Marker (wie am Testfall). Kommt vom
      // Server; nach manueller Korrektur eines KI-Feldes löscht der Server das *_en_ai-Flag.
      render: (r) => (
        <span className="flex items-center gap-2">
          <IdLabel id={r.human_id} secondary={showSourceRef ? r.source_ref : null} />
          {r.translation_review && <StateChip label={t('catalog.translationReview')} tone="warn" />}
        </span>
      ),
      // kein editor ⇒ festes Feld (Nummernkreis)
    },
    {
      // #112: die zweite Sicht — Stream > E2E-Prozess. Steht bewusst als ERSTE Spalte und die
      // Tabelle ist danach sortiert: gleiche Streams stehen beieinander, die Sicht ist damit im
      // Tab sichtbar, ohne CatalogTable um eine Gruppenebene erweitern zu muessen.
      //
      // BEDIENBAR wie jedes andere Feld dieser Tabelle (Abnahme-Befund 1). Vorgeschichte in zwei
      // Schritten: Zuerst stand hier ein <select>, der bei onChange sofort PATCH /e2e/:id/stream
      // feuerte — diese Route widerrief ueber `applyAssignment` Bestaetigungen (A8), ohne dass der
      // Nutzer mehr als "Gespeichert" sah. Daraufhin wurde die Spalte rein anzeigend und verwies
      // auf die Zuordnungsseite. Das erzeugte eine Sackgasse: In der Tabelle ging es gar nicht
      // mehr, und auf der Zuordnungsseite fand der Nutzer den Weg nicht.
      //
      // Jetzt beides: Bedienung als `editor` im Zeilen-Bedienmuster (#52) — Doppelklick oeffnet
      // die Zeile, Speichern schreibt —, aber ueber den Widerruf-festen Schreibpfad
      // (`moveStream`, siehe oben). Der Link auf die Zuordnungsseite bleibt fuer den Massenfall.
      key: 'stream',
      header: t('catalog.tabs.streams'),
      ...columnWidth(E2E_COLUMN_WIDTHS[0]),
      render: (r) => (
        <span className="text-text-body">{r.node_name ?? t('catalog.tree.withoutStream')}</span>
      ),
      editor: {
        kind: 'select',
        get: (d) => d.node_id,
        set: (d, v) => ({ ...d, node_id: v }),
        options: streamOptions,
        // A2: „ohne Stream" ist ein zulaessiger Zustand und deshalb eine regulaere Option — nicht
        // nur ein Anzeigetext, den man nicht wieder herstellen kann.
        allowEmpty: true,
        emptyLabel: t('catalog.tree.withoutStream'),
      },
    },
    {
      key: 'name_de',
      header: t('catalog.fields.nameDe'),
      ...columnWidth(E2E_COLUMN_WIDTHS[2]),
      // Task 10 (#63/#64): KI-Übersetzt-Badge am deutschen Namen, solange name_de_ai steht
      // (EN-Quell-Import). Spiegelbild von name_en unten.
      render: (r) => (
        <span className="flex items-center gap-1.5">
          <span>{r.name_de}</span>
          {r.name_de_ai && <KiTranslatedBadge />}
        </span>
      ),
      editor: { kind: 'text', get: (d) => d.name_de, set: (d, v) => ({ ...d, name_de: v }) },
    },
    {
      key: 'name_en',
      header: t('catalog.fields.nameEn'),
      ...columnWidth(E2E_COLUMN_WIDTHS[3]),
      // #49 (LNG-3): KI-Übersetzt-Badge am englischen Namen, solange name_en_ai steht (Muster wie
      // Schritt-Tabelle description_en/expected_output_en). Verschwindet nach manueller Korrektur.
      render: (r) => (
        <span className="flex items-center gap-1.5">
          <span>{r.name_en || '—'}</span>
          {r.name_en_ai && <KiTranslatedBadge />}
        </span>
      ),
      editor: { kind: 'text', get: (d) => d.name_en, set: (d, v) => ({ ...d, name_en: v }) },
    },
    {
      key: 'members',
      header: t('catalog.fields.stepCount'),
      ...columnWidth(E2E_COLUMN_WIDTHS[4]),
      // Abgeleitet ⇒ festes Feld. Traegt zugleich den Auf-/Zuklapp-Knopf fuer die
      // Mitgliederliste — der Knopf steht laut Spec in dieser Spalte.
      render: (r) => (
        <button
          type="button"
          onClick={() => setExpandedId((cur) => (cur === r.e2e_id ? null : r.e2e_id))}
          aria-label={t('catalog.actions.toggleMembers')}
          className="flex items-center gap-1 text-text-body hover:text-brand-accent"
        >
          <span className="text-text-muted">{expandedId === r.e2e_id ? '▾' : '▸'}</span>
          <span>{r.members.length}</span>
        </button>
      ),
    },
  ];

  if (isLoading) return <div className="p-6 text-sm text-text-muted">{t('catalog.loading')}</div>;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mb-3 flex flex-wrap items-baseline gap-3">
        <h2 className="text-sm font-bold text-text-primary">{t('catalog.tabs.e2e')}</h2>
        {/* TF-006 Kante C: der EINE Weg zum Umhaengen. Bewusst ein Link in der Kopfzeile und
            nicht je Zeile — n identische Links waeren Laerm, und die Zuordnungsseite arbeitet
            ohnehin mit ihrer eigenen Auswahl. `view=stream` oeffnet direkt die E2E-Sicht, damit
            der Weg nicht bei "Prozess-Sicht" beginnt. Nur mit `catalog.edit` sichtbar — ohne das
            Recht ist die Route gar nicht registriert (app-shell.tsx). */}
        {canEdit && (
          <Link to="/assignment?view=stream" className="text-xs text-brand-accent underline">
            {t('catalog.e2e.changeAssignment')}
          </Link>
        )}
      </div>
      {/* Befund 4: Filter + Gruppierung über der Tabelle. Wie in der Scope-Tabelle (#84) gelten
          sie für ALLE Rollen — es sind Lese-Operationen; nur das Bearbeiten hängt an
          `catalog.edit`. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-text-muted">
          {t('catalog.e2e.streamFilter')}
          <select
            value={streamParam}
            onChange={(e) => setStreamParam(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-body"
          >
            <option value="all">{t('catalog.e2e.streamFilterAll')}</option>
            {streamOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            <option value="none">{t('catalog.tree.withoutStream')}</option>
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-text-body">
          <input
            type="checkbox"
            checked={grouped}
            onChange={(e) => setGroupParam(e.target.checked ? 'stream' : 'none')}
          />
          {t('catalog.e2e.groupByStream')}
        </label>
        {/* Keine stille Kappung (Muster #78): sobald der Filter Zeilen wegnimmt, steht die Zahl
            daneben — sonst sieht ein leerer Stream aus wie ein leerer Katalog. */}
        {streamParam !== 'all' && (
          <span className="text-[11px] text-text-muted">
            {t('catalog.e2e.filterHiddenCount', { count: rows.length - filteredRows.length })}
          </span>
        )}
      </div>
      {searching && filteredRows.length === 0 && (
        <SearchEmptyNotice query={trimmedQuery} className="mb-3" />
      )}
      <CatalogTable
        columns={columns}
        rows={filteredRows}
        rowKey={(r) => r.e2e_id}
        toDraft={(r) => ({ name_de: r.name_de, name_en: r.name_en, node_id: r.node_id })}
        emptyDraft={{ name_de: '', name_en: '', node_id: null }}
        onCreate={async (d) => {
          // Zwei Schritte, weil die Kette beim Speichern noch gar nicht existiert: Erst anlegen
          // (die Route vergibt die e2e_id), dann — nur wenn ein Stream gewaehlt wurde — das Label
          // ueber denselben Widerruf-festen Weg wie beim Bearbeiten setzen.
          const created = await createChain.mutateAsync({ name_de: d.name_de, name_en: d.name_en });
          if (d.node_id === null) return;
          try {
            await moveStream.mutateAsync({ id: created.e2e_id, target: d.node_id });
          } catch (err) {
            // Bewusst NICHT weiterwerfen: Die Kette ist bereits angelegt. Wuerde der Fehler die
            // Zeile offen halten, legte der naechste Speichern-Klick eine ZWEITE Kette an. Statt
            // dessen die Meldung hier als Toast — die Zeile ist ja schon geschlossen — und der
            // Stream laesst sich an der vorhandenen Zeile per Doppelklick nachtragen.
            toast.error(mapApiError((k) => t(k), err));
          }
        }}
        onUpdate={async (id, d) => {
          // C1: Nur die tatsaechlich geaenderten Felder schicken — Muster aus step-table.tsx.
          // Der Server loescht name_de_ai/name_en_ai schon bei blosser Anwesenheit der
          // jeweiligen Keys im Body (catalog-e2e.ts). Ein Voll-Draft wuerde also z. B. beim
          // Korrigieren des englischen Namens BEIDE Uebersetzungs-Abzeichen loeschen -
          // unwiederbringlich, denn gesetzt werden sie nur vom Uebersetzungslauf.
          // Findet sich die Zeile nicht, wird der VOLLE Draft geschickt — nicht ein leeres Delta.
          // Ein leeres Delta wäre ein stiller No-op: die Zeile schlösse sich mit Erfolgsanschein,
          // die Eingabe wäre weg. Übervorsicht (ein Abzeichen zu viel gelöscht) ist hier der
          // billigere Fehler als Datenverlust. Dasselbe Gefälle wie in step-table.tsx.
          //
          // #112 (Abnahme-Befund 1): Der Stream ist NICHT Teil dieses Deltas — er hat seine eigene
          // Route (siehe `moveStream`). Deshalb wird das Delta ueber `NameDraft` gebildet und
          // nicht mehr ueber `Draft`: sonst landete `node_id` im Katalog-Body, wo der Server es
          // wortlos verwirft, und der Widerruf nach A8 unterbliebe still.
          const row = filteredRows.find((r) => r.e2e_id === id);
          const names: NameDraft = { name_de: d.name_de, name_en: d.name_en };
          if (!row) {
            await updateChain.mutateAsync({ id, d: names });
            return;
          }
          const before: NameDraft = { name_de: row.name_de, name_en: row.name_en };
          const patch = Object.fromEntries(
            (Object.keys(names) as (keyof NameDraft)[])
              .filter((k) => names[k] !== before[k])
              .map((k) => [k, names[k]]),
          );
          // Zwei getrennte Schreibpfade in EINEM Speichern-Klick. Reihenfolge: erst die Namen
          // (folgenlos), dann der Stream (loest ggf. den Widerruf aus). Scheitert der zweite
          // Schritt, bleibt die Zeile mit Fehler offen; ein erneutes Speichern rechnet das
          // Namens-Delta gegen die dann aktuelle Zeile neu und wiederholt nur noch den Stream.
          if (Object.keys(patch).length > 0) await updateChain.mutateAsync({ id, d: patch });
          if (d.node_id !== row.node_id) {
            await moveStream.mutateAsync({ id, target: d.node_id });
          }
        }}
        onDelete={(id) => deleteChain.mutateAsync(id)}
        deleteLabel={(r) => pickLang(lang, r.name_de, r.name_en)}
        canEdit={canEdit}
        addLabel={t('catalog.actions.addE2e')}
        // Abnahme-Befund 2: Ohne diese Angabe landete der Cursor beim Anlegen in der
        // Stream-AUSWAHL (erste Spalte mit editor). Wer dann losschreibt, bedient die
        // Tastatursuche des <select> — sichtbar passiert nichts, und genau so entsteht der
        // Eindruck, es gaebe nur ein Eingabefeld.
        defaultFocusColumn="name_de"
        // Befund 4 / A3: die Sicht „Stream → E2E-Prozess" als Gliederung, nicht nur als Spalte.
        // Ketten ohne Stream bekommen einen eigenen, benannten Kopf („Ohne Stream") statt still
        // ans Ende zu rutschen — A2: unzugeordnet ist ein zulässiger Zustand mit festem Namen.
        {...(grouped
          ? {
              groupBy: (r: E2eProcess) => ({
                key: r.node_id ?? 'none',
                label: r.node_name ?? t('catalog.tree.withoutStream'),
              }),
            }
          : {})}
        expanded={(r) =>
          expandedId === r.e2e_id ? (
            <E2eMembers projectId={projectId} chain={r} colSpan={columns.length + 1} />
          ) : null
        }
      />
    </div>
  );
}
